import { cookies } from 'services/i18n/utils';
import { isDOMAvailable } from 'utils/browser';

/**
 * The Partner's cookie consent state, expressed as the four categories the
 * signup flow gates tracking on. Defined here, at the vendor seam, and
 * re-exported by `hooks/useConsent` so the dependency arrow points hook →
 * service only.
 */
export interface Consent {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

/**
 * OneTrust consent category IDs. The "from-to" mapping is fixed by the company
 * OneTrust configuration (see the implementation guide):
 *
 * - C0001 Strictly Necessary  → essential
 * - C0002 Performance         → analytics
 * - C0003 Functional          → functional
 * - C0004 Social Media        → marketing
 */
export const ONE_TRUST_CATEGORY = {
  essential: 'C0001',
  analytics: 'C0002',
  functional: 'C0003',
  marketing: 'C0004',
} as const;

/** Nothing consented — the snapshot used until OneTrust confirms (default-deny). */
const DEFAULT_DENY: Consent = Object.freeze({
  essential: false,
  analytics: false,
  marketing: false,
  functional: false,
});

const listeners = new Set<() => void>();

// useSyncExternalStore compares snapshots by reference, so getSnapshot must
// return a stable object until the underlying consent actually changes.
let cachedKey: string | undefined;
let cachedSnapshot: Consent = DEFAULT_DENY;

function parseActiveGroups(raw: string | undefined): Consent {
  if (!raw) {
    return DEFAULT_DENY;
  }

  const active = new Set(raw.split(',').filter(Boolean));

  return {
    essential: active.has(ONE_TRUST_CATEGORY.essential),
    analytics: active.has(ONE_TRUST_CATEGORY.analytics),
    functional: active.has(ONE_TRUST_CATEGORY.functional),
    marketing: active.has(ONE_TRUST_CATEGORY.marketing),
  };
}

/** Cookie OneTrust persists the consent decision in (also seeded by migration). */
const OPTANON_CONSENT_COOKIE = 'OptanonConsent';

/**
 * Consent parsed from a persisted `OptanonConsent` cookie's `groups` segment
 * (e.g. `groups=C0001:1,C0002:0,...`). Returns `undefined` when there is no
 * decision to read, so the caller can stay default-deny.
 */
function parseConsentCookie(value: string | undefined): Consent | undefined {
  const segment = value && /groups=([^&]*)/.exec(value)?.[1];

  if (!segment) {
    return undefined;
  }

  // Real OneTrust cookies can keep the groups segment percent-encoded even after
  // the cookie value itself is decoded; normalize before splitting.
  const groups = segment.includes('%') ? decodeURIComponent(segment) : segment;

  const granted = new Set(
    groups
      .split(',')
      .map(pair => pair.split(':'))
      .filter(([, state]) => state === '1')
      .map(([id]) => id),
  );

  return {
    essential: granted.has(ONE_TRUST_CATEGORY.essential),
    analytics: granted.has(ONE_TRUST_CATEGORY.analytics),
    functional: granted.has(ONE_TRUST_CATEGORY.functional),
    marketing: granted.has(ONE_TRUST_CATEGORY.marketing),
  };
}

/**
 * Resolves consent from the live OneTrust global when present, otherwise from
 * the persisted `OptanonConsent` cookie. The cookie fallback keeps trackers
 * loading for returning/migrated Partners even if the OneTrust script never
 * loads (CDN blocked or misconfigured); new visitors have no cookie and stay
 * default-deny until they accept. Returns a cache key so getSnapshot can keep a
 * stable reference across calls.
 */
function readConsent(): { key: string; consent: Consent } {
  if (!isDOMAvailable()) {
    return { key: '', consent: DEFAULT_DENY };
  }

  const liveGroups = globalThis.window.OnetrustActiveGroups;

  if (liveGroups) {
    return {
      key: `live:${liveGroups}`,
      consent: parseActiveGroups(liveGroups),
    };
  }

  const cookie = cookies.get(OPTANON_CONSENT_COOKIE, { doNotParse: true }) as
    | string
    | undefined;
  const consent = parseConsentCookie(cookie);

  return consent
    ? { key: `cookie:${cookie}`, consent }
    : { key: '', consent: DEFAULT_DENY };
}

function notify() {
  listeners.forEach(listener => listener());
}

const OPTANON_WRAPPER_TAG = '__ssuConsentWrapper';

/** Our `OptanonWrapper` carries a tag so we can detect (and not re-install) it. */
type TaggedWrapper = (() => void) & { [OPTANON_WRAPPER_TAG]?: boolean };

/** Event OneTrust dispatches on the window when the user changes consent (not on load). */
const CONSENT_CHANGED_EVENT = 'consent.onetrust';

/**
 * Formats granted categories as the comma-separated preferences string used by
 * the legacy consent-change analytics event, e.g. "marketing,analytics,essential".
 */
export function formatGrantedPreferences(consent: Consent): string {
  return (['marketing', 'analytics', 'functional', 'essential'] as const)
    .filter(category => consent[category])
    .join(',');
}

/**
 * Installs a `window.OptanonWrapper` that fans OneTrust's "loaded / consent
 * changed" callback out to our subscribers. Idempotent and safe to call before
 * the OneTrust script has loaded.
 */
function ensureOptanonWrapper() {
  if (!isDOMAvailable()) {
    return;
  }

  const current: TaggedWrapper | undefined = globalThis.window.OptanonWrapper;

  if (current?.[OPTANON_WRAPPER_TAG]) {
    return;
  }

  const wrapper = Object.assign(() => notify(), {
    [OPTANON_WRAPPER_TAG]: true,
  });

  globalThis.window.OptanonWrapper = wrapper;
}

/**
 * Thin wrapper around the OneTrust browser SDK. This module is the single seam
 * between the app and the vendor global — consumers and tests depend on it, not
 * on `window.OneTrust`/`window.OnetrustActiveGroups` directly.
 */
export const oneTrustClient = {
  /** Subscribe to consent changes. Returns an unsubscribe function. */
  subscribe(listener: () => void): () => void {
    ensureOptanonWrapper();
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  },

  /**
   * Current consent, derived from `window.OnetrustActiveGroups` when OneTrust is
   * live, falling back to the persisted `OptanonConsent` cookie otherwise (see
   * readConsent).
   */
  getSnapshot(): Consent {
    const { key, consent } = readConsent();

    if (key !== cachedKey) {
      cachedKey = key;
      cachedSnapshot = consent;
    }

    return cachedSnapshot;
  },

  /** Default-deny snapshot for non-browser/SSR rendering. */
  getServerSnapshot(): Consent {
    return DEFAULT_DENY;
  },

  /**
   * Subscribe to *user* consent changes (fires on opt-in/out, not on initial
   * load — unlike `subscribe`). Returns an unsubscribe function.
   */
  onConsentChanged(listener: () => void): () => void {
    if (!isDOMAvailable()) {
      return () => undefined;
    }

    const handler = () => listener();
    globalThis.window.addEventListener(CONSENT_CHANGED_EVENT, handler);

    return () =>
      globalThis.window.removeEventListener(CONSENT_CHANGED_EVENT, handler);
  },
};
